import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * Whether the page currently on screen is still a checkout.
 *
 * The header strips itself down during checkout — no navigation, no account
 * menu — because anything that invites a click there is a way to lose a
 * half-finished payment. But /booking/:id serves two pages: the checkout
 * before payment, and the confirmation after it, and only the URL is the same.
 * Keeping the stripped header on a confirmation leaves someone who has just
 * paid with no way back into the site, on the one page that then invites them
 * to book the rest of their trip.
 *
 * The header cannot work this out for itself: only the page knows whether the
 * booking it fetched is settled. So the page says, and this carries the answer
 * up. Default true, because the pre-payment state is the one where being wrong
 * is expensive.
 */

const CheckoutModeContext = createContext({ isCheckout: true, setIsCheckout: () => {} });

export function CheckoutModeProvider({ children }) {
  const [isCheckout, setIsCheckout] = useState(true);
  const value = useMemo(() => ({ isCheckout, setIsCheckout }), [isCheckout]);
  return <CheckoutModeContext.Provider value={value}>{children}</CheckoutModeContext.Provider>;
}

export function useCheckoutMode() {
  return useContext(CheckoutModeContext);
}

/** Called by a page that is a confirmation, not a checkout. */
export function useDeclareConfirmation() {
  const { setIsCheckout } = useCheckoutMode();
  return useCallback(() => setIsCheckout(false), [setIsCheckout]);
}

export default CheckoutModeContext;
